import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import proposalsApi from '../../../APIs/proposals';
import techniciansJobApi from '../../../APIs/technicians';
import { ReactComponent as CloseSVG } from '../../../assets/icons/cross.svg';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import {
  BrowsedClientInformation,
  BrowsedCostAndShipping,
  BrowsedJobInformation,
  BrowsedPropertyInformation,
} from '../../../components/TechnicianBrowsedJobDetails';
import escConstants from '../../../constants';
import {
  BrowseFile,
  Button,
  DatePicker,
  Divider,
  FormContainer,
  Input,
  NavigateBack,
  SelectDropdown,
  TextArea,
  Typography,
} from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import { FileWithKeys, requestAPI, uploadFilesToS3 } from '../../../utils';
import { handleKeyDownForNumberOnly } from '../../../utils/keyboardHandlerEvents';
import { ViewJobContainerStyled } from '../BrowseJobDetails/style';
import {
  CloseAdditionalWrapperStyled,
  // CreateProposalContainerStyled,
  SplitInputContainerStyled,
} from './style';

const MAX_COST = 999999999;
const MAX_EXPERIENCE = 80;

type PastClientTypes = {
  client: string;
  id: number;
};
type InitialStatesType = {
  technicianJobId: string;
  clientId: string;
  experience: string;
  numberOfExperience: number;
  costEstimation: string;
  qualificaionOrCertification: string;
  pastClients: PastClientTypes[];
  startingDate: Date;
  endingDate: Date;
  workApproach: string;
  riskAssessmentOrInspection: string;
  designApproach: string;
  specification: string;
  proposal?: string;

  notes: string;
  proposalType: string;
  pastReferences: File[];
};

const validationSchema = yup.object({
  experience: yup.string().required('Enter your experience.'),
  numberOfExperience: yup.number().required('Enter experience (in years).'),
  costEstimation: yup.string().required('Enter your proposed cost.'),
  qualificaionOrCertification: yup
    .string()
    .required('Enter your valid qualification or certification.'),
  pastClients: yup.array().of(
    yup.object({
      client: yup.string().required('Client is required.'),
    })
  ),
  startingDate: yup.string().required('Enter your possible starting date.'),
  endingDate: yup.string().required('Enter your deadline.'),
  workApproach: yup.string().required('Select your working approach.'),
  riskAssessmentOrInspection: yup
    .string()
    .required('Select your risk assessment approach.'),
  designApproach: yup.string().required('Enter your design approach.'),
  specification: yup.string().required('Enter specification.'),
  notes: yup.string().required('Summarize your proposal.'),
  pastReferences: yup
    .array()
    .nullable()
    .min(1, 'At least 1 past references is required.'),
});

const MAX_NUMBER_OF_PROJECT = 4;

const CreateProposal = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [numberLimitError, setNumberLimitError] = useState({
    cost: '',
    experience: '',
  });

  const formikRef = useRef<FormikProps<InitialStatesType>>(null);
  const [files, setFiles] = useState<File[]>([]);

  const [isOpen, setIsOpen] = useState(false);

  const jobDetails = useSelector(
    (state: RootState) => state.technicianJobsDetails
  );

  const Client = jobDetails?.createdBy?._id;

  const getJobDetails = async (id: any) => {
    try {
      await requestAPI(techniciansJobApi.getTechnicianJobsDetail(id));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getJobDetails(jobId);
  }, [jobId]);

  const [initialStates, _setInitialStates] = useState<InitialStatesType>({
    technicianJobId: jobId || '',
    clientId: Client || '',
    proposalType: 'TECHNICIAN',
    experience: '',
    numberOfExperience: 1,
    costEstimation: '',
    qualificaionOrCertification: '',
    pastClients: [
      {
        client: '',
        id: 1,
      },
    ],
    startingDate: new Date(),
    endingDate: new Date(),
    workApproach: '',
    riskAssessmentOrInspection: '',
    designApproach: '',
    specification: '',
    notes: '',
    pastReferences: [],
  });

  const handleCreateProposal = async (
    values: InitialStatesType,
    { resetForm }: FormikHelpers<InitialStatesType>
  ) => {
    try {
      setLoading(true);
      let fileWithKeys: FileWithKeys[] = [];
      if (files && files.length > 0) {
        fileWithKeys = (await uploadFilesToS3(files)) as FileWithKeys[];
      }

      if (fileWithKeys && fileWithKeys.length === files.length) {
        const result = await requestAPI(
          proposalsApi.createProposals({
            technicianJobId: values.technicianJobId,
            experience: values.experience,
            numberOfExperience: values.numberOfExperience,
            cost: values.costEstimation,
            qualifications: values.qualificaionOrCertification,
            pastClients: values.pastClients.map((past) => past.client),
            startingDate: values.startingDate,
            endDate: values.endingDate,
            workApproach: values.workApproach,
            riskAssessment: values.riskAssessmentOrInspection,
            designApproach: values.designApproach,
            specification: values.specification,
            notes: values.notes,
            proposalType: values.proposalType,
            pastProject: fileWithKeys.map((fwk) => fwk.key),
          })
        );
        if (result) {
          toast(escConstants.toastMessages.proposalCreated);
        }
        navigate(-1);
        resetForm();
      }
    } catch (error: any) {
      toast('Error');
      console.error(error);
    } finally {
      setLoading(false);
      setIsOpen(false);
      setLoading(false);
    }
  };

  const handleAddPastClients = () => {
    if (formikRef && formikRef.current) {
      const pastClients: PastClientTypes[] =
        formikRef.current.values.pastClients;
      const newClient = {
        client: '',
        id: pastClients.length + 1,
      };
      formikRef.current.setFieldValue('pastClients', [
        ...pastClients,
        newClient,
      ]);
    }
  };

  const handleRemovePastClients = (idx: number) => {
    if (formikRef && formikRef.current) {
      const pastClients: PastClientTypes[] =
        formikRef.current.values.pastClients;

      pastClients.splice(idx, 1);

      formikRef.current.setFieldValue('pastClients', [...pastClients]);
    }
  };

  return (
    <ViewJobContainerStyled>
      <div>
        <NavigateBack />
      </div>
      <div className="contents">
        <Typography variant="title">Create a Proposal</Typography>
        <Divider spaceY="20px" />
        <BrowsedJobInformation
          jobCreatedAt="12 aug 2023 - 12:59 am"
          jobTitle={jobDetails?.jobTitle || ''}
          notes={jobDetails?.notes || ''}
        />
        <Divider spaceY="20px" />
        <BrowsedPropertyInformation
          addressOfProperty={jobDetails?.building?.buildingAddress || ''}
          buildingName={jobDetails?.building?.buildingName || ''}
          buildingId="id_1"
        />
        <Divider spaceY="20px" />
        <BrowsedClientInformation
          name={jobDetails?.createdBy?.fullName || ''}
          avatar=""
          email={jobDetails?.createdBy?.emailAddress || ''}
        />
        <Divider spaceY="20px" />
        <BrowsedCostAndShipping
          pricing={jobDetails?.pricing || ''}
          shippingAddress={jobDetails?.shippingAddress || ''}
        />
        <Divider spaceY="20px" />
        <div className="form-container">
          <Formik
            innerRef={formikRef}
            initialValues={initialStates}
            onSubmit={handleCreateProposal}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({ values, setFieldValue, errors, touched }) => {
              return (
                <Form>
                  <SplitInputContainerStyled>
                    <FormContainer
                      label="Experience Title"
                      error={
                        touched.experience && errors.experience
                          ? errors.experience
                          : ''
                      }
                    >
                      <Input
                        placeholder="Your most eligible experience "
                        value={values.experience}
                        onChange={(e) => {
                          const { value } = e.target;
                          setFieldValue('experience', value);
                        }}
                      />
                    </FormContainer>
                    <FormContainer
                      label="Number of Experience (in years)"
                      error={
                        touched.numberOfExperience && errors.numberOfExperience
                          ? errors.numberOfExperience
                          : ''
                      }
                    >
                      <div>
                        <Input
                          placeholder="Your number of experience"
                          type="number"
                          min={0}
                          max={MAX_EXPERIENCE}
                          value={values.numberOfExperience}
                          onChange={(e) => {
                            let { value } = e.target;

                            // Enforce maximum value of MAX_PRICE
                            if (parseInt(value) > MAX_EXPERIENCE) {
                              value = `${MAX_EXPERIENCE}`;
                              setNumberLimitError((prev) => ({
                                ...prev,
                                experience: `Experience has max limit of ${MAX_EXPERIENCE}`,
                              }));
                            } else {
                              setNumberLimitError((prev) => ({
                                ...prev,
                                experience: '',
                              }));
                            }
                            setFieldValue('numberOfExperience', value);
                          }}
                          onKeyDown={handleKeyDownForNumberOnly}
                        />
                        {numberLimitError.experience && (
                          <Typography fontColor="dark" size={12}>
                            {numberLimitError.experience}
                          </Typography>
                        )}
                      </div>
                    </FormContainer>
                  </SplitInputContainerStyled>
                  <FormContainer
                    label="Cost Estimation"
                    error={
                      touched.costEstimation && errors.costEstimation
                        ? errors.costEstimation
                        : ''
                    }
                  >
                    <div>
                      <Input
                        placeholder="Proposed Cost"
                        type="number"
                        min={0}
                        max={MAX_COST}
                        value={values.costEstimation}
                        onChange={(e) => {
                          let { value } = e.target;

                          // Enforce maximum value of MAX_PRICE
                          if (parseInt(value) > MAX_COST) {
                            value = `${MAX_COST}`;
                            setNumberLimitError((prev) => ({
                              ...prev,
                              cost: `Proposed Cost has max limit of ${MAX_COST}`,
                            }));
                          } else {
                            setNumberLimitError((prev) => ({
                              ...prev,
                              cost: '',
                            }));
                          }
                          setFieldValue('costEstimation', value);
                        }}
                        onKeyDown={handleKeyDownForNumberOnly}
                      />
                      {numberLimitError?.cost && (
                        <Typography fontColor="dark" size={12}>
                          {numberLimitError?.cost}
                        </Typography>
                      )}
                    </div>
                  </FormContainer>
                  <FormContainer
                    label="Add Reference of Past Project"
                    error={
                      touched.pastReferences &&
                      values.pastReferences.length === 0 &&
                      typeof errors.pastReferences === 'string'
                        ? errors.pastReferences
                        : ''
                    }
                  >
                    <BrowseFile
                      files={files}
                      setFiles={(selectedfiles: File[]) => {
                        selectedfiles.splice(
                          MAX_NUMBER_OF_PROJECT,
                          selectedfiles.length - MAX_NUMBER_OF_PROJECT
                        );
                        setFieldValue('pastReferences', files);
                        setFieldValue('pastReferences', selectedfiles);
                        setFiles(selectedfiles);
                      }}
                      multiple
                      accepts=".jpg, .png, .jpeg, .svg, .pdf"
                      showFileBrowser={
                        values.pastReferences.length < MAX_NUMBER_OF_PROJECT
                      }
                    />
                  </FormContainer>
                  <FormContainer
                    label="Qualifications or Certifications"
                    subLabel="Most applicable certification/qualifications."
                    error={
                      touched.qualificaionOrCertification &&
                      errors.qualificaionOrCertification
                        ? errors.qualificaionOrCertification
                        : ''
                    }
                  >
                    <Input
                      placeholder="Qualifications or Certifications"
                      value={values.qualificaionOrCertification}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('qualificaionOrCertification', value);
                      }}
                    />
                  </FormContainer>

                  {values.pastClients &&
                    values.pastClients.map((client, idx) => (
                      <div key={`${client.id}-${idx}`}>
                        <CloseAdditionalWrapperStyled>
                          <CloseSVG
                            onClick={() => handleRemovePastClients(idx)}
                          />
                        </CloseAdditionalWrapperStyled>
                        <FormContainer
                          label={`Past Clients ${
                            values.pastClients.length > 1 ? `(${idx + 1})` : ''
                          }`}
                          error={
                            touched?.pastClients &&
                            touched?.pastClients[idx]?.client
                              ? errors?.pastClients &&
                                (
                                  errors?.pastClients[idx] as FormikErrors<{
                                    client: string;
                                    id: string;
                                  }>
                                )?.client
                              : ''
                          }
                        >
                          <Input
                            placeholder="Add name of your past clients"
                            value={values.pastClients[idx].client}
                            onChange={(e) => {
                              const { value } = e.target;
                              const updatedValues = [...values.pastClients];
                              updatedValues[idx].client = value;

                              setFieldValue('pastClients', updatedValues);
                            }}
                          />
                        </FormContainer>
                      </div>
                    ))}
                  <Button
                    variant="gray"
                    size="sm"
                    color="#666"
                    onClick={handleAddPastClients}
                  >
                    Add More Clients
                  </Button>
                  <Typography style={{ margin: '30px 0px 10px' }}>
                    Timeline of project
                  </Typography>
                  <SplitInputContainerStyled>
                    <FormContainer
                      label="Starting Date"
                      error={
                        touched.startingDate &&
                        errors.startingDate &&
                        typeof errors.startingDate === 'string'
                          ? errors.startingDate
                          : ''
                      }
                    >
                      <DatePicker
                        onChange={(date) => {
                          setFieldValue('startingDate', date);
                        }}
                        value={values.startingDate}
                        placeholder="Starting Date"
                        minDate={Date.now()}
                      />
                    </FormContainer>
                    <FormContainer
                      label="Ending Date"
                      error={
                        touched.endingDate &&
                        errors.endingDate &&
                        typeof errors.endingDate === 'string'
                          ? errors.endingDate
                          : ''
                      }
                    >
                      <DatePicker
                        onChange={(date) => {
                          setFieldValue('endingDate', date);
                        }}
                        value={values.endingDate}
                        placeholder="Ending Date"
                        minDate={values.startingDate}
                      />
                    </FormContainer>
                  </SplitInputContainerStyled>
                  <FormContainer
                    label="Work Approach"
                    subLabel="How do you intend to collect data?"
                    error={
                      touched.workApproach && errors.workApproach
                        ? errors.workApproach
                        : ''
                    }
                  >
                    <SelectDropdown
                      selected={values.workApproach}
                      onSelect={(value: string) => {
                        setFieldValue('workApproach', value);
                      }}
                      options={[
                        {
                          label: 'Onsite Assessment',
                          value: 'onsite-assessment',
                        },
                        {
                          label: 'Rely on provided Building Layout',
                          value: 'rely-on-building-layout',
                        },
                        {
                          label: 'Collaborative Design Workshop',
                          value: 'collaborative-desing',
                        },
                        {
                          label: 'Prototype Iteration',
                          value: 'prototype-iteration',
                        },
                      ]}
                    />
                  </FormContainer>
                  <FormContainer
                    label="Risk Assessment"
                    subLabel="Are you going to conduct risk assessment before starting work?"
                    error={
                      touched.riskAssessmentOrInspection &&
                      errors.riskAssessmentOrInspection
                        ? errors.riskAssessmentOrInspection
                        : ''
                    }
                  >
                    <SelectDropdown
                      selected={values.riskAssessmentOrInspection}
                      onSelect={(value: string) => {
                        setFieldValue('riskAssessmentOrInspection', value);
                      }}
                      options={[
                        {
                          label: 'No Risk Assessment',
                          value: 'no-risk-assessment',
                        },
                        {
                          label: 'Rely on provided Building Layout',
                          value: 'rely-on-building-layout',
                        },
                        {
                          label: 'Onsite assessment/inspection',
                          value: 'onsite-assessment-inspection',
                        },
                      ]}
                    />
                  </FormContainer>
                  <FormContainer
                    label="Design Approach"
                    subLabel="Explain your design approach."
                    error={
                      touched.designApproach && errors.designApproach
                        ? errors.designApproach
                        : ''
                    }
                  >
                    <TextArea
                      maxLength={2000}
                      showCount
                      maxRow={6}
                      value={values.designApproach}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('designApproach', value);
                      }}
                    />
                  </FormContainer>
                  <FormContainer
                    label="Specification"
                    subLabel="Why did you choose above design approach?"
                    error={
                      touched.specification && errors.specification
                        ? errors.specification
                        : ''
                    }
                  >
                    <TextArea
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('specification', value);
                      }}
                      value={values.specification}
                      maxLength={1000}
                      showCount
                    />
                  </FormContainer>
                  <FormContainer
                    label="Notes"
                    subLabel="Summarize your proposal."
                    error={touched.notes && errors.notes ? errors.notes : ''}
                  >
                    <TextArea
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue('notes', value);
                      }}
                      value={values.notes}
                      maxLength={800}
                      showCount
                    />
                  </FormContainer>
                  <Button
                    variant="secondary"
                    type={isOpen ? 'button' : 'submit'}
                    size="lg"
                    width="161px"
                    onClick={async () => {
                      if (formikRef.current) {
                        await formikRef.current.validateForm();
                        formikRef.current.setTouched({
                          clientId: true,
                          costEstimation: true,
                          designApproach: true,
                          endingDate: true,
                          experience: true,
                          notes: true,
                          numberOfExperience: true,
                          pastClients: [{ client: true }],
                          pastReferences: [{ name: true }],
                          proposal: true,
                          proposalType: true,
                          qualificaionOrCertification: true,
                          riskAssessmentOrInspection: true,
                          specification: true,
                          startingDate: true,
                          technicianJobId: true,
                          workApproach: true,
                        });
                        if (Object.keys(formikRef.current.errors).length > 0) {
                          return;
                        }
                        setIsOpen(true);
                      }
                    }}
                  >
                    Send a Proposal
                  </Button>
                  <ConfirmationModal
                    title="Confirmation"
                    message="Are you sure you want to submit a proposal?"
                    cancelText="No"
                    okText="Yes"
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    onOk={() => formikRef.current?.handleSubmit()}
                    loading={loading}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </ViewJobContainerStyled>
  );
};

export default CreateProposal;
